/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { bookingService } from "@src/@api-services/booking";

// ** Types
interface Booking {
  id: string | number;
  businessName: string;
  clientName: string;
  serviceName: string;
  addons: number;
  staffAssigned: string;
  date: string;
  time: string;
  locationType: string;
  bookingStatus: string;
  paymentStatus: string;
  totalAmount: number;
  duration: number;
  createdAt: string;
}

interface GetBookingsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  bookingStatus?: string;
  paymentStatus?: string;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  [key: string]: any;
}

interface BookingsState {
  data: Booking[];
  total: number;
  params: GetBookingsParams;
  allData: Booking[];
  selectedBooking: any | null;
  loading: boolean;
  error: string | null;
}

// ** Async Thunks
export const getBookings = createAsyncThunk(
  "bookings/getBookings",
  async (params: GetBookingsParams, { rejectWithValue }) => {
    try {
      const response = await bookingService.getBookings(params);
      return {
        params,
        data: response.data.data.data,
        total: response.data.data.totalRecord,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch bookings"
      );
    }
  }
);

export const getBooking = createAsyncThunk(
  "bookings/getBooking",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await bookingService.getBooking(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch booking"
      );
    }
  }
);

export const addBooking = createAsyncThunk(
  "bookings/addBooking",
  async (bookingData: any, { dispatch, getState, rejectWithValue }: any) => {
    try {
      const response = await bookingService.createBooking(bookingData);
      const state = getState();
      await dispatch(getBookings(state.bookings.params));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create booking"
      );
    }
  }
);

export const updateBooking = createAsyncThunk(
  "bookings/updateBooking",
  async (
    { id, bookingData }: { id: string | number; bookingData: any },
    { dispatch, getState, rejectWithValue }: any
  ) => {
    try {
      const response = await bookingService.updateBooking(id, bookingData);
      const state = getState();
      await dispatch(getBookings(state.bookings.params));
      await dispatch(getBooking(id));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update booking"
      );
    }
  }
);

export const deleteBooking = createAsyncThunk(
  "bookings/deleteBooking",
  async (id: string | number, { dispatch, getState, rejectWithValue }: any) => {
    try {
      await bookingService.deleteBooking(id);
      const state = getState();
      await dispatch(getBookings(state.bookings.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete booking"
      );
    }
  }
);

const initialState: BookingsState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: 'id',
    orderDirection: 'ASC'
  },
  allData: [],
  selectedBooking: null,
  loading: false,
  error: null,
};

export const bookingsSlice = createSlice({
  name: "bookings",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedBooking: (state) => {
      state.selectedBooking = null;
    },
    setParams: (state, action) => {
      state.params = { ...state.params, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.params = action.payload.params;
        state.total = action.payload.total;
      })
      .addCase(getBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedBooking = action.payload;
      })
      .addCase(getBooking.rejected, (state) => {
        state.loading = false;
        state.selectedBooking = null;
      });
  },
});

export const { clearError, clearSelectedBooking, setParams } = bookingsSlice.actions;

export default bookingsSlice.reducer;
